/**
 * Factory function to create a scrolly rectangle entity with all demo components
 */
import { atom } from "jotai";
import type { WorldState } from "#scripts/vmc/WorldState.ts";
import { World } from "#scripts/vmc/WorldState.ts";
import {
  CActions,
  CChangeOrigin,
  CCollaborators,
  CFeatureFlags,
  CFocus,
  CInlineChart,
  CSelection,
  CSuggestions,
  CUndoStack,
  CValidationState,
  CValue,
  CVersionComparison,
  ScrollyRectangleEntity,
} from "./components.ts";

/**
 * Creates and returns a scrolly rectangle entity with default demo values
 */
export function createScrollyRectangle(world: WorldState): World.UID {
  const rectangleUID = World.uid(null, null, "scrolly-rectangle");

  world.addEntity(rectangleUID, ScrollyRectangleEntity, {
    value: CValue.of({
      valueAtom: atom("42"),
    }),
    "feature-flags": CFeatureFlags.of({
      flagsAtom: atom(new Set(["base", "presence"])),
    }),
    "validation-state": CValidationState.of({
      isValidAtom: atom(true),
      errorAtom: atom<string | null>(null),
    }),
    "undo-stack": CUndoStack.of({
      historyAtom: atom(["42"]),
      indexAtom: atom(0),
    }),
    "version-comparison": CVersionComparison.of({
      baselineAtom: atom("42"),
      hasChangesAtom: atom(false),
    }),
    "change-origin": CChangeOrigin.of({
      authorAtom: atom("Alice"),
      timestampAtom: atom(new Date(Date.now() - 120000)), // 2m ago
    }),
    collaborators: CCollaborators.of({
      cursorsAtom: atom([
        { userId: "Bob", position: 1, color: "#3b82f6" },
        { userId: "Charlie", position: 2, color: "#10b981" },
      ]),
    }),
    selection: CSelection.of({
      selectedRangeAtom: atom<[number, number] | null>(null),
    }),
    focus: CFocus.of({
      focusPositionAtom: atom(0),
      isFocusedAtom: atom(false),
    }),
    actions: CActions.of({
      availableActionsAtom: atom([
        { id: "copy", label: "Copy", keybinding: "Cmd+C" },
        { id: "paste", label: "Paste", keybinding: "Cmd+V" },
        { id: "undo", label: "Undo", keybinding: "Cmd+Z" },
      ]),
    }),
    suggestions: CSuggestions.of({
      suggestionsAtom: atom([
        { text: "100", confidence: 0.9 },
        { text: "50", confidence: 0.7 },
      ]),
    }),
    "inline-chart": CInlineChart.of({
      dataPointsAtom: atom([10, 25, 42, 38, 50]),
    }),
  });

  return rectangleUID;
}
